/**
 * Retry utilities - Shared between web and mobile
 *
 * Provides retry logic with exponential backoff for failed operations.
 * Used by OfflineContext (web) and can be used by mobile components.
 */

/**
 * Retry with exponential backoff
 *
 * @param fn - Function to retry
 * @param maxAttempts - Maximum number of retry attempts (default: 5)
 * @param baseDelay - Base delay in milliseconds (default: 1000)
 * @returns Promise that resolves when function succeeds or throws after max attempts
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 5,
  baseDelay: number = 1000
): Promise<T> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      return await fn();
    } catch (error) {
      attempts++;

      if (attempts >= maxAttempts) {
        console.error(`[retryWithBackoff] Max attempts (${maxAttempts}) reached`);
        throw error;
      }

      // Exponential backoff: baseDelay * 2^(attempts-1)
      const delay = baseDelay * Math.pow(2, attempts - 1);
      console.log(`[retryWithBackoff] Attempt ${attempts} failed, retrying in ${delay}ms`);

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error('retryWithBackoff: Unexpected state');
}
